/**
 * Admin section for the O365 email integration (singleton email_settings).
 *
 * Security properties (matching the server contract):
 * - client_secret is WRITE-ONLY: the server returns only has_client_secret. When
 *   a secret is already stored and the field is left untouched, we omit it (keep).
 * - Storing a secret requires the app-side crypto key; the UI disables the field
 *   and explains when crypto_available is false.
 */
import { useEffect, useState } from "preact/hooks";
import { Mail, Send, CheckCircle2, XCircle } from "lucide-preact";
import type { EmailSettingsDto, EmailSettingsUpdate } from "@juuplan/shared";
import { getEmailSettings, updateEmailSettings, testEmailSettings } from "../lib/api";
import { toastError, toastSuccess } from "../lib/toast";

import { Button } from "./ui/Button";

export const EmailSettingsIcon = Mail;

interface Draft {
  enabled: boolean;
  tenant_id: string;
  client_id: string;
  system_sender: string;
  send_on_behalf_enabled: boolean;
}

export function EmailSettingsSection(): preact.JSX.Element {
  const [dto, setDto] = useState<EmailSettingsDto | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editSecret, setEditSecret] = useState(false);
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  // Ohne `loadError` blieb `dto` bei einem Fehler null — die Sektion zeigte
  // dann DAUERHAFT „Lädt…", obwohl der Abruf schon gescheitert war. Der Toast
  // allein reicht nicht: er verschwindet nach wenigen Sekunden.
  const [loadError, setLoadError] = useState(false);

  const load = (): void => {
    setLoadError(false);
    void getEmailSettings().then(
      (d) => {
        setDto(d);
        setDraft({
          enabled: d.enabled,
          tenant_id: d.tenant_id ?? "",
          client_id: d.client_id ?? "",
          system_sender: d.system_sender ?? "",
          send_on_behalf_enabled: d.send_on_behalf_enabled,
        });
        setEditSecret(false);
        setSecret("");
      },
      () => {
        setLoadError(true);
        toastError("E-Mail-Einstellungen konnten nicht geladen werden");
      }
    );
  };

  useEffect(load, []);

  if (loadError) {
    return (
      <p class="text-sm text-[color:var(--color-error)]">
        E-Mail-Einstellungen konnten nicht geladen werden.{" "}
        <button type="button" onClick={load} class="underline">Erneut laden</button>
      </p>
    );
  }

  if (!dto || !draft) {
    return (
      <p class="text-sm text-[color:var(--color-on-surface-variant)]">Lädt…</p>
    );
  }

  const patch = (p: Partial<Draft>): void => setDraft({ ...draft, ...p });

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const body: EmailSettingsUpdate = {
        enabled: draft.enabled,
        tenant_id: draft.tenant_id.trim() || null,
        client_id: draft.client_id.trim() || null,
        system_sender: draft.system_sender.trim() || null,
        send_on_behalf_enabled: draft.send_on_behalf_enabled,
      };
      // Only send client_secret when the admin chose to change it.
      if (editSecret) body.client_secret = secret;
      const updated = await updateEmailSettings(body);
      setDto(updated);
      setEditSecret(false);
      setSecret("");
      toastSuccess("E-Mail-Einstellungen gespeichert");
    } catch (err) {
      toastError((err as Error)?.message ?? "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (): Promise<void> => {
    setTesting(true);
    try {
      const res = await testEmailSettings();
      if (res.ok) toastSuccess("Test-E-Mail versendet — bitte Posteingang prüfen");
      else toastError(`Test fehlgeschlagen: ${res.reason ?? "unbekannt"}`);
    } catch {
      toastError("Test fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  const label = "block text-sm font-medium text-[color:var(--color-on-surface)] mb-1";
  const input =
    "w-full rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]";

  return (
    <section class="max-w-2xl space-y-6">
      <header class="flex items-center gap-2">
        <Mail size={16} class="text-[color:var(--color-primary)]" aria-hidden="true" />
        <h2 class="text-lg font-semibold text-[color:var(--color-on-surface)]">
          E-Mail-Anbindung (Microsoft 365)
        </h2>
      </header>

      {/* Setup guide + required O365 permissions */}
      <details class="rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
        <summary class="cursor-pointer select-none px-3 py-2 text-sm font-medium text-[color:var(--color-on-surface)]">
          Einrichtung &amp; benötigte O365-Berechtigungen
        </summary>
        <div class="space-y-4 border-t border-[color:var(--color-outline-variant)] px-3 py-3 text-sm text-[color:var(--color-on-surface-variant)]">
          <div>
            <p class="mb-1 font-medium text-[color:var(--color-on-surface)]">Kurzanleitung</p>
            <ol class="list-decimal space-y-1 pl-5">
              <li>In Entra (Azure AD) die App-Registrierung öffnen — dieselbe App wie für den juuplan-Login.</li>
              <li>
                Unter <span class="font-medium">API-Berechtigungen</span> die unten genannten
                Microsoft-Graph-Rechte hinzufügen und <span class="font-medium">Administrator&shy;zustimmung erteilen</span>.
              </li>
              <li>
                Unter <span class="font-medium">Zertifikate &amp; Geheimnisse</span> ein
                Client-Secret erzeugen und hier zusammen mit Tenant-ID, Client-ID und
                System-Absender eintragen.
              </li>
              <li>„E-Mail-Versand aktiviert" einschalten, speichern und mit „Test-E-Mail" prüfen.</li>
              <li>
                Für „Versand im Namen der Nutzer" den Toggle aktivieren — jeder Nutzer erteilt
                seinen Consent danach selbst (siehe unten).
              </li>
            </ol>
          </div>
          <div>
            <p class="mb-1 font-medium text-[color:var(--color-on-surface)]">
              Wie &amp; wo Nutzer ihren Consent geben
            </p>
            <ol class="list-decimal space-y-1 pl-5">
              <li>
                Nutzer öffnen <span class="font-medium">Einstellungen → Postfach-Verbindung</span>
                (nur sichtbar, wenn der Toggle oben aktiv ist) und klicken auf
                <span class="font-medium"> „Verbinden"</span>.
              </li>
              <li>
                Sie werden zu Microsoft weitergeleitet und bestätigen dort einmalig den
                Zustimmungs&shy;dialog für <code class="rounded bg-[color:var(--color-surface-container)] px-1">Mail.Send</code>.
                Wurde tenantweiter Admin-Consent erteilt, entfällt dieser Dialog und die
                Verbindung erfolgt direkt.
              </li>
              <li>
                Danach steht der Status auf „Postfach verbunden"; Auto-Nachfragen gehen aus dem
                eigenen Postfach raus. „Trennen" bzw. „Erneut verbinden" ist jederzeit möglich
                (bei abgelaufener Anmeldung).
              </li>
            </ol>
          </div>
          <div>
            <p class="mb-1 font-medium text-[color:var(--color-on-surface)]">
              Benötigte Microsoft-Graph-Berechtigungen
            </p>
            <ul class="list-disc space-y-1.5 pl-5">
              <li>
                <span class="font-medium">Anwendung (Application)</span> ·{" "}
                <code class="rounded bg-[color:var(--color-surface-container)] px-1">Mail.Send</code>{" "}
                — Versand der System-Benachrichtigungen aus dem System-Postfach. Admin-Consent nötig.
                Empfohlen: per <span class="italic">ApplicationAccessPolicy</span> auf genau das
                System-Postfach einschränken (sonst darf die App jedes Postfach nutzen).
              </li>
              <li>
                <span class="font-medium">Delegiert (Delegated)</span> ·{" "}
                <code class="rounded bg-[color:var(--color-surface-container)] px-1">Mail.Send</code>{" "}
                und{" "}
                <code class="rounded bg-[color:var(--color-surface-container)] px-1">offline_access</code>{" "}
                — nur für „Versand im Namen der Nutzer". Tenant-weiter Admin-Consent empfohlen;
                andernfalls bestätigt jeder Nutzer die Zustimmung beim Verbinden selbst.
              </li>
            </ul>
            <p class="mt-2 text-xs">
              Redirect-URI: der bestehende SSO-Callback wird wiederverwendet — kein neuer Eintrag
              nötig. Voraussetzung: System-Postfach bzw. Nutzer mit Exchange-Online-Postfach. Für
              gespeicherte Secrets muss serverseitig ein Verschlüsselungs-Key gesetzt sein.
            </p>
          </div>
        </div>
      </details>

      {!dto.crypto_available && (
        <div class="rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container)] p-3 text-sm text-[color:var(--color-on-surface-variant)]">
          Kein Verschlüsselungs-Key gesetzt (<code>MAIL_TOKEN_ENCRYPTION_KEY</code> oder
          <code> GIT_ENCRYPTION_KEY</code>). Client-Secret und delegierter Versand sind
          deaktiviert, bis ein Key konfiguriert ist.
        </div>
      )}

      {/* Master switch */}
      <label class="flex items-center justify-between gap-4 rounded-md border border-[color:var(--color-outline-variant)] p-3">
        <span>
          <span class="block text-sm font-medium text-[color:var(--color-on-surface)]">
            E-Mail-Versand aktiviert
          </span>
          <span class="block text-xs text-[color:var(--color-on-surface-variant)]">
            System-Benachrichtigungen werden per E-Mail aus dem System-Postfach versendet.
          </span>
        </span>
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => patch({ enabled: (e.target as HTMLInputElement).checked })}
          class="size-5 accent-[color:var(--color-primary)]"
          aria-label="E-Mail-Versand aktiviert"
        />
      </label>

      <div>
        <label class={label} for="es-tenant">Tenant-ID</label>
        <input
          id="es-tenant"
          class={input}
          value={draft.tenant_id}
          placeholder="Fällt auf M365_TENANT_ID (env) zurück, wenn leer"
          onInput={(e) => patch({ tenant_id: (e.target as HTMLInputElement).value })}
        />
      </div>

      <div>
        <label class={label} for="es-client">Client-ID</label>
        <input
          id="es-client"
          class={input}
          value={draft.client_id}
          placeholder="Fällt auf M365_CLIENT_ID (env) zurück, wenn leer"
          onInput={(e) => patch({ client_id: (e.target as HTMLInputElement).value })}
        />
      </div>

      <div>
        <label class={label} for="es-secret">Client-Secret</label>
        {dto.has_client_secret && !editSecret ? (
          <div class="flex items-center gap-3">
            <span class="text-sm text-[color:var(--color-on-surface-variant)]">•••••• (gesetzt)</span>
            <button
              type="button"
              class="text-sm text-[color:var(--color-primary)] hover:underline disabled:opacity-50"
              disabled={!dto.crypto_available}
              onClick={() => {
                setEditSecret(true);
                setSecret("");
              }}
            >
              Ändern
            </button>
          </div>
        ) : (
          <input
            id="es-secret"
            type="password"
            class={input}
            value={secret}
            disabled={!dto.crypto_available}
            placeholder={dto.crypto_available ? "Neues Client-Secret eingeben" : "Kein Crypto-Key gesetzt"}
            onInput={(e) => {
              setEditSecret(true);
              setSecret((e.target as HTMLInputElement).value);
            }}
          />
        )}
      </div>

      <div>
        <label class={label} for="es-sender">System-Absender (E-Mail)</label>
        <input
          id="es-sender"
          type="email"
          class={input}
          value={draft.system_sender}
          placeholder="z. B. noreply@juunit.com"
          onInput={(e) => patch({ system_sender: (e.target as HTMLInputElement).value })}
        />
      </div>

      {/* Send-on-behalf toggle */}
      <label class="flex items-center justify-between gap-4 rounded-md border border-[color:var(--color-outline-variant)] p-3">
        <span>
          <span class="block text-sm font-medium text-[color:var(--color-on-surface)]">
            Versand im Namen der Nutzer
          </span>
          <span class="block text-xs text-[color:var(--color-on-surface-variant)]">
            Nutzer können ihr Postfach verbinden; Auto-Nachfragen gehen dann aus deren
            eigenem Postfach raus (delegiert, benötigt einmaliges Consent).
          </span>
        </span>
        <input
          type="checkbox"
          checked={draft.send_on_behalf_enabled}
          disabled={!dto.crypto_available}
          onChange={(e) =>
            patch({ send_on_behalf_enabled: (e.target as HTMLInputElement).checked })
          }
          class="size-5 accent-[color:var(--color-primary)]"
          aria-label="Versand im Namen der Nutzer"
        />
      </label>

      <div class="flex items-center gap-3 pt-2">
        <Button
          variant="primary"
          size="lg"
          type="button"
          onClick={() => void save()}
          disabled={saving}
        >
          <CheckCircle2 size={16} aria-hidden="true" />
          {saving ? "Speichere…" : "Speichern"}
        </Button>
        <button
          type="button"
          onClick={() => void runTest()}
          disabled={testing || !dto.enabled}
          title={dto.enabled ? "" : "Erst aktivieren und speichern"}
          class="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-outline-variant)] px-4 py-2 text-sm text-[color:var(--color-on-surface)] disabled:opacity-50"
        >
          <Send size={16} aria-hidden="true" />
          {testing ? "Sende…" : "Test-E-Mail"}
        </button>
        <button
          type="button"
          onClick={load}
          class="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
        >
          <XCircle size={16} aria-hidden="true" />
          Verwerfen
        </button>
      </div>
    </section>
  );
}
